import React, { useEffect, useState } from "react";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import { Row } from "react-bootstrap";
import RangeSlider from "react-bootstrap-range-slider";
import swal from "sweetalert";
import Swal from "sweetalert2";
import axios from "axios";
import moment from "moment-timezone";
import CountdownDisplay from "./../Dashboard/elements/CountdownDisplay";

const PromptBanner = ({ icon, title, text }) => (
  <div
    className="d-flex align-items-start"
    style={{
      marginTop: 6,
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 10,
      background: "linear-gradient(180deg,#F7FBFF 0%,#EEF6FF 100%)",
      border: "1px solid #D7E8FF",
      boxShadow: "0 0 0 2px rgba(7,104,255,0.03) inset",
    }}
  >
    <div style={{ fontSize: 18, lineHeight: 1, marginRight: 10 }}>{icon}</div>
    <div>
      {title && (
        <div style={{ fontWeight: 700, color: "#0B3D6E" }}>{title}</div>
      )}
      <div style={{ color: "#355B82" }}>{text}</div>
    </div>
  </div>
);

const A23_PROMPTS = {
  step1: "Select “Reveal” on the predictions page for the current year.",
  step2: "You will see “Start your NYE Party”, select “Ok”.",
  step3:
    "Slide the button from “Sealed” to “Reveal”. The prompt “Ready for the Big Reveal?” appears. Select “Ok”.",
  step4: (year) =>
    `After all members have scored how well they predicted the future, it’s time to close the year. Select “Close the “${year}” Party.`,
};

const PredictionsViewData = ({
  predictionsData,
  presentYear,
  storedUserId,
  updateTrigger,
  setUpdateTrigger,
}) => {
  const [storedRoleEnhanced, setStoredRoleEnhanced] = useState("?");
  const [currentGroupRole, setCurrentGroupRole] = useState("?");
  const [isLocked, setIsLocked] = useState(false);
  const [isSealed, setIsSealed] = useState(false);
  const [visibility, setVisibility] = useState(true);
  const [answerability, setAnswerability] = useState(false);
  const [launchEvent, setLaunchEvent] = useState(true);
  const [startScore, setStartScore] = useState(false);
  const [sliderValue, setSliderValue] = useState({});
  const [countdown, setCountdown] = useState(null);
  const [flip, setFlip] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [predictionsGroupRole, setPredictionsGroupRole] = useState(null);

  const storedUser = localStorage.getItem("userDetails");
  const storedDetails = JSON.parse(storedUser || "{}");
  const storedRole = storedDetails?.data?.role;

  const handleInputChange = (taskid, value) => {
    setInputValues((prev) => ({ ...prev, [taskid]: value }));
  };

  const bumpUpdate = () => {
    if (typeof setUpdateTrigger === "function") {
      setUpdateTrigger((v) => (typeof v === "number" ? v + 1 : !v));
    }
  };

  const updateGame = async (payload) => {
    const groupName = localStorage.getItem("user_initial_Group");
    const encodedGroupName = encodeURIComponent(groupName || "");
    await axios.put(
      `${process.env.REACT_APP_PREDICTIONS_API}/api/update-game/${encodedGroupName}/${presentYear}`,
      payload
    );
  };

  const handleLockChange = () => {
    swal({
      text: isLocked ? `Lock the predictions?` : `Open the predictions now?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      const nextIsLocked = !isLocked;

      const nextState = nextIsLocked
        ? {
            isLocked: true,
            isSealed: false,
            visibility: true,
            answerability: true,
            launchEvent: true,
            startScore: false,
            gameStatus: "?",
            currentPlay: "answerNow",
          }
        : {
            isLocked: false,
            isSealed: false,
            visibility: true,
            answerability: false,
            launchEvent: true,
            startScore: false,
            gameStatus: "?",
            currentPlay: "answerLocked",
          };

      setIsLocked(nextState.isLocked);
      setIsSealed(nextState.isSealed);
      setVisibility(nextState.visibility);
      setAnswerability(nextState.answerability);
      setLaunchEvent(nextState.launchEvent);
      setStartScore(nextState.startScore);

      try {
        await updateGame(nextState);
        bumpUpdate();
      } catch (e) {
        console.error(e);
      }
    });
  };

  const handleToggleChange = () => {
    swal({
      text: startScore ? `Seal the answers now?` : `Ready for the big reveal?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      const nextStartScore = !startScore;

      const nextState = nextStartScore
        ? {
            isLocked: false,
            isSealed: false,
            visibility: true,
            answerability: false,
            launchEvent: true,
            startScore: true,
            gameStatus: "open",
            currentPlay: "beginScoring",
          }
        : {
            isLocked: false,
            isSealed: false,
            visibility: true,
            answerability: false,
            launchEvent: true,
            startScore: false,
            gameStatus: "open",
            currentPlay: "sealed",
          };

      setIsLocked(nextState.isLocked);
      setIsSealed(nextState.isSealed);
      setVisibility(nextState.visibility);
      setAnswerability(nextState.answerability);
      setLaunchEvent(nextState.launchEvent);
      setStartScore(nextState.startScore);

      try {
        await updateGame(nextState);
        bumpUpdate();
      } catch (e) {
        console.error(e);
      }
    });
  };

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const userId = storedUserId;
        const groupName = localStorage.getItem("user_initial_Group");
        const responseRole = await axios.get(
          `${process.env.REACT_APP_PREDICTIONS_API}/api/user-group-role`,
          { params: { userId, groupName } }
        );
        setPredictionsGroupRole(responseRole?.data?.userGroupRole ?? null);
      } catch (error) {
        console.error("Error fetching group role:", error);
      }
    };

    const calculateCountdown = () => {
      const userTz = moment.tz.guess();
      // Target midnight January 1st of next year in user's local timezone
      const newYearDate = moment.tz(`${presentYear + 1}-01-01T00:00:00`, userTz);
      const userDateTime = moment.tz(userTz);
      setCountdown(moment.duration(newYearDate.diff(userDateTime)));
    };

    fetchRole();
    calculateCountdown();
    const countdownInterval = setInterval(calculateCountdown, 5000);
    return () => clearInterval(countdownInterval);
  }, [storedUserId, presentYear]);

  const handleLaunchEvent = async () => {
    swal({
      title: "Start your NYE Party?",
      icon: "warning",
      buttons: ["Cancel", "OK"],
    }).then(async (value) => {
      if (!value) return;

      const nextState = {
        isLocked: false,
        isSealed: false,
        visibility: true,
        answerability: false,
        launchEvent: true,
        startScore: false,
        gameStatus: "open",
        currentPlay: "sealed",
      };

      setIsLocked(nextState.isLocked);
      setIsSealed(nextState.isSealed);
      setVisibility(nextState.visibility);
      setAnswerability(nextState.answerability);
      setLaunchEvent(nextState.launchEvent);
      setStartScore(nextState.startScore);

      try {
        await updateGame(nextState);
        bumpUpdate();
      } catch (e) {
        console.error(e);
      }
    });
  };

  const handleSliderChange = (taskId, newScore) => {
    setSliderValue((prev) => ({ ...prev, [taskId]: newScore }));
  };

  const handleSaveScores = async () => {
    const scoresToUpdate = Object.entries(sliderValue).map(
      ([taskid, score]) => ({
        taskid,
        score,
      })
    );

    try {
      const groupName = localStorage.getItem("user_initial_Group");
      const encodedGroupName = encodeURIComponent(groupName || "");

      await axios.post(
        `${process.env.REACT_APP_PREDICTIONS_API}/api/save-scores/${encodedGroupName}/${presentYear}`,
        { userId: storedUserId, scores: scoresToUpdate }
      );

      Swal.fire("Thank you!", "Scores saved.", "success");
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveAnswers = async () => {
    try {
      const userId = storedUserId;
      const groupName = localStorage.getItem("user_initial_Group");
      const year = presentYear;

      if (!year) {
        console.error("Year is null or undefined.");
        return;
      }

      const answersWithMetadata = Object.entries(inputValues)
        .map(([taskid, value]) => ({
          taskid,
          answer: value,
          year,
          userId,
          groupName,
        }))
        .filter((x) => (x.answer || "").trim() !== "");

      await axios.post(
        `${process.env.REACT_APP_PREDICTIONS_API}/api/save-answers`,
        { answersWithMetadata }
      );

      swal("Answers saved successfully!", { icon: "success" });
    } catch (error) {
      console.error("Error saving answers:", error);
      swal("Error saving answers", { icon: "error" });
    }
  };

  const resetParty = async () => {
    Swal.fire({
      title: `Start over ${presentYear}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, let's play again!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const nextState = {
        isLocked: false,
        isSealed: false,
        visibility: true,
        answerability: false,
        launchEvent: true,
        startScore: false,
        gameStatus: "?",
        currentPlay: "answerNow",
      };

      setIsLocked(nextState.isLocked);
      setIsSealed(nextState.isSealed);
      setVisibility(nextState.visibility);
      setAnswerability(nextState.answerability);
      setLaunchEvent(nextState.launchEvent);
      setStartScore(nextState.startScore);

      try {
        await updateGame(nextState);
        bumpUpdate();
      } catch (e) {
        console.error(e);
      }
    });
  };

  const closeParty = async () => {
    Swal.fire({
      title: `Are you sure you want to close the ${presentYear} Party?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, we had fun!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const nextState = {
        isLocked: false,
        isSealed: false,
        visibility: true,
        answerability: false,
        launchEvent: true,
        startScore: false,
        gameStatus: "closed",
        currentPlay: "-",
      };

      setIsLocked(nextState.isLocked);
      setIsSealed(nextState.isSealed);
      setVisibility(nextState.visibility);
      setAnswerability(nextState.answerability);
      setLaunchEvent(nextState.launchEvent);
      setStartScore(nextState.startScore);

      try {
        await updateGame(nextState);
        Swal.fire({
          title: `Closed ${presentYear}!`,
          icon: "success",
          confirmButtonText: "See you next year!",
        });
        bumpUpdate();
      } catch (e) {
        console.error(e);
      }
    });
  };

  const fetchRoleData = async () => {
    try {
      const chkrUserRole = await axios.get(
        `${process.env.REACT_APP_PREDICTIONS_API}/api/getUserGroupsAdmin/${storedUserId}`
      );

      const userGroupsInfo = chkrUserRole?.data?.data?.userGroups || [];
      const hasGroupAdminRole = userGroupsInfo.some(
        (group) => group.role === "groupAdmin"
      );

      setCurrentGroupRole(
        userGroupsInfo.find(
          (group) =>
            group.groupName === localStorage.getItem("user_initial_Group")
        )?.role || "not-admin"
      );

      setStoredRoleEnhanced(hasGroupAdminRole ? "groupAdmin" : storedRole);
    } catch (error) {
      console.error("Error fetching data:", error);
      setStoredRoleEnhanced(storedRole);
    }
  };

  useEffect(() => {
    fetchRoleData();
  }, []);

  useEffect(() => {
    predictionsData.forEach((prediction) => {
      if (prediction.currentPlay === "answerLocked") {
        setIsLocked(false);
        setIsSealed(false);
        setVisibility(true);
        setAnswerability(false);
        setLaunchEvent(true);
        setStartScore(false);
      } else if (prediction.currentPlay === "answerNow") {
        setIsLocked(true);
        setIsSealed(false);
        setVisibility(true);
        setAnswerability(true);
        setLaunchEvent(true);
        setStartScore(false);
      } else if (prediction.currentPlay === "sealed") {
        setIsLocked(false);
        setIsSealed(false);
        setVisibility(true);
        setAnswerability(false);
        setLaunchEvent(true);
        setStartScore(false);
      } else if (prediction.currentPlay === "beginScoring") {
        setIsLocked(false);
        setIsSealed(false);
        setVisibility(true);
        setAnswerability(false);
        setLaunchEvent(true);
        setStartScore(true);
      }
    });
  }, [predictionsData]);

  return (
    <div className="col">
      <div
        className="card kanbanPreview-bx"
        style={{ backgroundColor: "#F3F3F4" }}
      >
        <div>
          {predictionsData &&
            predictionsData.length > 0 &&
            predictionsData.map((prediction) => (
              <div key={prediction._id}>
                {storedRoleEnhanced === "groupAdmin" &&
                  currentGroupRole === "groupAdmin" && (
                    <div
                      style={{
                        marginLeft: "10px",
                        marginTop: "2px",
                        display: "flex",
                        alignItems: "center",
                        height: "65px",
                      }}
                    >
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{
                          height: "40px",
                          width: "50%",
                          marginLeft: "-10px",
                          marginTop: "12px",
                          backgroundColor: "#01152F",
                          borderColor: "#01152F",
                        }}
                        onClick={resetParty}
                      >
                        Reset {presentYear} Party?
                      </button>
                      <span style={{ width: 36 }} />
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{
                          height: "40px",
                          width: "50%",
                          marginLeft: "-10px",
                          marginTop: "12px",
                          backgroundColor: "#FF0000",
                          borderColor: "#FF0000",
                        }}
                        onClick={closeParty}
                      >
                        Close {presentYear} Party?
                      </button>
                    </div>
                  )}

                {prediction.mode === "missing" ? (
                  <div style={{ backgroundColor: "#01152F" }}>
                    <div
                      style={{
                        backgroundColor: "#01152F",
                        textAlign: "center",
                      }}
                    >
                      <h1>&nbsp;</h1>
                      <CountdownDisplay
                        countdown={countdown}
                        setCountdown={setCountdown}
                        flip={flip}
                        setFlip={setFlip}
                      />
                      <h1>&nbsp;</h1>
                      <h5 style={{ color: "#FF8888" }}>
                        &nbsp;&nbsp;Please contact your group admin
                      </h5>
                      <h5 style={{ color: "#FFAAAA" }}>
                        &nbsp;&nbsp;(no Predictions data found)
                      </h5>
                      <h1>&nbsp;</h1>
                    </div>
                  </div>
                ) : (
                  <div>
                    {prediction.gameStatus === "?" && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "65px",
                          }}
                        >
                          {currentGroupRole === "groupAdmin" &&
                          (storedRoleEnhanced === "groupAdmin" ||
                            predictionsGroupRole === "groupAdmin") &&
                          !isSealed &&
                          !startScore ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                height: "65px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "1px",
                                  fontSize: "14px",
                                  verticalAlign: "top",
                                  marginTop: "2px",
                                  color: "#01152F",
                                  fontWeight: "bold",
                                }}
                              >
                                Locked&nbsp;
                              </span>
                              <Toggle
                                checked={isLocked}
                                onChange={handleLockChange}
                              />
                              <span
                                style={{
                                  padding: "1px",
                                  fontSize: "14px",
                                  verticalAlign: "top",
                                  marginTop: "2px",
                                  color: "#01152F",
                                  fontWeight: "bold",
                                }}
                              >
                                &nbsp;Open
                              </span>
                            </div>
                          ) : (
                            <div>
                              <h3
                                style={{ color: "#01152F", marginTop: "4px" }}
                              >
                                Let's celebrate!
                              </h3>
                            </div>
                          )}

                          {launchEvent &&
                            storedRoleEnhanced === "groupAdmin" &&
                            currentGroupRole === "groupAdmin" && (
                              <div
                                style={{
                                  marginLeft: "60px",
                                  marginTop: "-10px",
                                  display: "flex",
                                  alignItems: "center",
                                  height: "65px",
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{
                                    height: "40px",
                                    width: "120px",
                                    marginLeft: "-10px",
                                    marginTop: "12px",
                                    backgroundColor: "red",
                                    borderColor: "red",
                                  }}
                                  onClick={handleLaunchEvent}
                                >
                                  Reveal
                                </button>
                              </div>
                            )}
                        </div>

                        {launchEvent &&
                          storedRoleEnhanced === "groupAdmin" &&
                          currentGroupRole === "groupAdmin" && (
                            <PromptBanner
                              title="The Big Reveal"
                              text={A23_PROMPTS.step1}
                            />
                          )}
                      </div>
                    )}

                    {prediction.gameStatus === "open" && (
                      <Row className="justify-content-between align-items-center">
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "65px",
                            }}
                          >
                            {currentGroupRole === "groupAdmin" &&
                            (storedRoleEnhanced === "groupAdmin" ||
                              predictionsGroupRole === "groupAdmin") ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  height: "65px",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "1px",
                                    fontSize: "14px",
                                    verticalAlign: "top",
                                    marginTop: "2px",
                                    color: "#01152F",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Sealed&nbsp;
                                </span>
                                <Toggle
                                  checked={startScore}
                                  disabled={startScore}
                                  onChange={handleToggleChange}
                                />
                                <span
                                  style={{
                                    padding: "1px",
                                    fontSize: "14px",
                                    verticalAlign: "top",
                                    marginTop: "2px",
                                    color: "#01152F",
                                    fontWeight: "bold",
                                  }}
                                >
                                  &nbsp;Reveal
                                </span>
                              </div>
                            ) : (
                              <div>
                                <h3
                                  style={{ color: "#01152F", marginTop: "4px" }}
                                >
                                  Let's celebrate!
                                </h3>
                              </div>
                            )}
                          </div>
                        </div>

                        {startScore && currentGroupRole === "groupAdmin" && (
                          <PromptBanner text={A23_PROMPTS.step4(presentYear)} />
                        )}

                        {currentGroupRole === "groupAdmin" &&
                          prediction.currentPlay === "sealed" && (
                            <PromptBanner text={A23_PROMPTS.step3} />
                          )}
                      </Row>
                    )}

                    {prediction.gameStatus === "closed" && (
                      <Row className="justify-content-between align-items-center">
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "65px",
                            }}
                          >
                            <span
                              style={{
                                padding: "1px",
                                fontSize: "14px",
                                verticalAlign: "top",
                                marginTop: "2px",
                                color: "#01152F",
                                fontWeight: "bold",
                              }}
                            >
                              Sealed&nbsp;
                            </span>
                            <Toggle checked disabled />
                            <span
                              style={{
                                padding: "1px",
                                fontSize: "14px",
                                verticalAlign: "top",
                                marginTop: "2px",
                                color: "#01152F",
                                fontWeight: "bold",
                              }}
                            >
                              &nbsp;Reveal
                            </span>
                          </div>
                        </div>
                        <p>
                          The {presentYear} Party is now closed! Get ready for
                          next year’s predictions.
                        </p>
                      </Row>
                    )}

                    {prediction.questions.map(
                      (question) =>
                        question.text !== "(new prediction question)" && (
                          <div
                            className="sub-card p-0"
                            key={question.taskid}
                            style={{
                              width: "96%",
                              marginLeft: "6px",
                              marginBottom: "20px",
                            }}
                          >
                            <div
                              className="task-card-data"
                              style={{ backgroundColor: "#F3F3F4" }}
                            >
                              <div className="row">
                                <div
                                  className=""
                                  style={{
                                    backgroundColor: "#FFF8AF",
                                    border: "1px solid #F1D34F",
                                  }}
                                >
                                  <h3 style={{ color: "#000000" }}>
                                    {question.text}
                                  </h3>
                                </div>

                                {prediction.gameStatus === "?" &&
                                  answerability && (
                                    <div className="">
                                      {(() => {
                                        const myBlock = (
                                          prediction.memberAnswers || []
                                        ).find(
                                          (x) => x.userId === storedUserId
                                        );
                                        const myExisting =
                                          myBlock?.answers?.find(
                                            (a) => a.taskid === question.taskid
                                          );

                                        return (
                                          <div
                                            style={{
                                              padding: "10px",
                                              marginLeft: "-11px",
                                            }}
                                          >
                                            <input
                                              className="form-control"
                                              type="text"
                                              value={
                                                inputValues[question.taskid] ??
                                                (myExisting
                                                  ? myExisting.answer
                                                  : "")
                                              }
                                              style={{ height: "40px" }}
                                              onChange={(e) =>
                                                handleInputChange(
                                                  question.taskid,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </div>
                                        );
                                      })()}
                                      Please save your answers when done. Once
                                      locked, your answers will be revealed at
                                      the New Year's party!
                                    </div>
                                  )}

                                {prediction.gameStatus === "closed" ? (
                                  <div
                                    className="col-md-12"
                                    style={{
                                      minHeight: "60px",
                                      paddingTop: "3px",
                                      backgroundColor: "#F3F3F4",
                                    }}
                                  >
                                    <span>
                                      <h6
                                        style={{ backgroundColor: "#F3F3F4" }}
                                      >
                                        {prediction.memberAnswers.map(
                                          (memberAnswer) => {
                                            const matchingAnswer =
                                              memberAnswer.answers.find(
                                                (answer) =>
                                                  answer.taskid ===
                                                  question.taskid
                                              );
                                            if (
                                              memberAnswer.userId ===
                                                storedUserId &&
                                              matchingAnswer
                                            ) {
                                              return (
                                                <React.Fragment
                                                  key={memberAnswer.userId}
                                                >
                                                  {matchingAnswer.answer && (
                                                    <div className="footer-data-container">
                                                      <div className="footer-data d-flex flex-row">
                                                        <h4>
                                                          {
                                                            matchingAnswer.answer
                                                          }
                                                        </h4>
                                                      </div>
                                                      <div>
                                                        <h6>&nbsp;</h6>
                                                      </div>
                                                      <div
                                                        className="footer-data d-flex flex-row align-items-center justify-content-center"
                                                        style={{
                                                          height: "30px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <div>
                                                          <RangeSlider
                                                            value={
                                                              matchingAnswer.score
                                                            }
                                                            tooltip="off"
                                                            step={25}
                                                            min={0}
                                                            max={100}
                                                            disabled
                                                          />
                                                        </div>
                                                        <div>
                                                          <h4>
                                                            &nbsp;Scored:{" "}
                                                            {
                                                              matchingAnswer.score
                                                            }
                                                          </h4>
                                                        </div>
                                                      </div>
                                                      <div
                                                        className="footer-data d-flex flex-row align-items-center justify-content-center"
                                                        style={{
                                                          height: "30px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        <span>
                                                          <font color="gray">
                                                            {new Date(
                                                              matchingAnswer.answerTime
                                                            ).toLocaleString(
                                                              "en-US"
                                                            )}
                                                          </font>
                                                        </span>
                                                      </div>
                                                    </div>
                                                  )}
                                                </React.Fragment>
                                              );
                                            }
                                            return null;
                                          }
                                        )}
                                      </h6>
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    className="col-md-12"
                                    style={{
                                      minHeight: "60px",
                                      paddingTop: "3px",
                                      backgroundColor: "#F3F3F4",
                                    }}
                                  >
                                    {visibility ? (
                                      <div>
                                        <span>
                                          <h6
                                            style={{
                                              backgroundColor: "#F3F3F4",
                                            }}
                                          >
                                            {prediction.memberAnswers.map(
                                              (memberAnswer) => {
                                                const matchingAnswer =
                                                  memberAnswer.answers.find(
                                                    (answer) =>
                                                      answer.taskid ===
                                                      question.taskid
                                                  );
                                                if (
                                                  memberAnswer.userId ===
                                                    storedUserId &&
                                                  matchingAnswer
                                                ) {
                                                  return (
                                                    <React.Fragment
                                                      key={memberAnswer.userId}
                                                    >
                                                      {matchingAnswer.answer &&
                                                        startScore && (
                                                          <div className="footer-data-container">
                                                            <div
                                                              className="footer-data d-flex flex-row"
                                                              style={{
                                                                height: "20px",
                                                              }}
                                                            >
                                                              <span>
                                                                <font color="gray">
                                                                  {new Date(
                                                                    matchingAnswer.answerTime
                                                                  ).toLocaleString(
                                                                    "en-US"
                                                                  )}
                                                                </font>
                                                              </span>
                                                            </div>
                                                            <div className="footer-data d-flex flex-row">
                                                              <h4>
                                                                {
                                                                  matchingAnswer.answer
                                                                }
                                                              </h4>
                                                            </div>
                                                            <div className="footer-data d-flex flex-row align-items-center justify-content-center">
                                                              <h6>&nbsp;</h6>
                                                            </div>
                                                            <div className="footer-data d-flex flex-row align-items-center justify-content-center">
                                                              <h5
                                                                style={{
                                                                  color:
                                                                    "#AA0000",
                                                                }}
                                                              >
                                                                Please score
                                                                your answer
                                                              </h5>
                                                            </div>
                                                            <div
                                                              className="footer-data d-flex flex-row align-items-center justify-content-center"
                                                              style={{
                                                                height: "30px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              <div>
                                                                <div className="footer-data d-flex flex-row">
                                                                  <RangeSlider
                                                                    tooltip="off"
                                                                    step={25}
                                                                    min={0}
                                                                    max={100}
                                                                    onChange={(
                                                                      e
                                                                    ) => {
                                                                      handleSliderChange(
                                                                        question.taskid,
                                                                        Number(
                                                                          e
                                                                            .target
                                                                            .value
                                                                        )
                                                                      );
                                                                    }}
                                                                    variant="danger"
                                                                    value={
                                                                      sliderValue[
                                                                        question
                                                                          .taskid
                                                                      ] ??
                                                                      matchingAnswer.score
                                                                    }
                                                                  />
                                                                </div>
                                                              </div>
                                                              <span>
                                                                <h1> </h1>
                                                              </span>
                                                              <div>
                                                                <div className="footer-data d-flex flex-row">
                                                                  <h4>
                                                                    &nbsp;Score:{" "}
                                                                    <font color="#AA0000">
                                                                      {sliderValue[
                                                                        question
                                                                          .taskid
                                                                      ] ??
                                                                        matchingAnswer.score}
                                                                    </font>
                                                                  </h4>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        )}
                                                    </React.Fragment>
                                                  );
                                                }
                                                return null;
                                              }
                                            )}
                                          </h6>
                                        </span>
                                      </div>
                                    ) : (
                                      <div
                                        className="footer-data d-flex flex-row align-items-center justify-content-center"
                                        style={{
                                          height: "35px",
                                          textAlign: "center",
                                        }}
                                      >
                                        <h6 style={{ color: "#01152F" }}>
                                          please wait for the grand reveal
                                        </h6>
                                      </div>
                                    )}

                                    {prediction.gameStatus === "?" &&
                                      !answerability && (
                                        <div className="footer-data d-flex flex-row align-items-center justify-content-center">
                                          {launchEvent && (
                                            <div className="footer-data d-flex flex-row align-items-center justify-content-center">
                                              <h6 style={{ color: "#01152F" }}>
                                                Stay Tuned! Group Admin will
                                                open the answer fields and you
                                                can enter your predictions!
                                              </h6>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                    {prediction.currentPlay === "sealed" &&
                                      visibility &&
                                      !answerability && (
                                        <div className="footer-data d-flex flex-row align-items-center justify-content-center">
                                          <h6 style={{ color: "#01152F" }}>
                                            please wait for the grand reveal
                                          </h6>
                                        </div>
                                      )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                    )}
                  </div>
                )}

                {answerability && (
                  <div className="ms-3 mt-n1 d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-primary ms-n2 mt-2"
                      onClick={handleSaveAnswers}
                    >
                      Save Answers
                    </button>
                  </div>
                )}

                {visibility && prediction.currentPlay === "beginScoring" && (
                  <div
                    style={{
                      marginLeft: "20px",
                      marginTop: "-10px",
                      display: "flex",
                      alignItems: "center",
                      height: "65px",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{
                        height: "40px",
                        width: "155px",
                        marginLeft: "-10px",
                        marginTop: "12px",
                      }}
                      disabled={!startScore}
                      onClick={handleSaveScores}
                    >
                      Save Scores
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default PredictionsViewData;
